import { lazy, Suspense, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  CheckSquare,
  LayoutDashboard,
  Search,
  Settings,
  Smartphone,
  Target,
} from 'lucide-react';
import { useMe } from '@/auth/useMe';
import { useAuth } from '@/auth/AuthProvider';
import { NewVersionBanner } from '@/components/NewVersionBanner';
import { ThemeToggle } from '@/components/ThemeToggle';
import { CommandPalette } from '@/components/CommandPalette';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Tree-shaking the dev switcher out of a production build.
 *
 * `__DEV_LOGIN__` is a `define` — substituted as a literal before Rollup runs —
 * so when it is false this is a constant `null` and the dynamic import is
 * removed, along with the module and everything it pulls in. A plain static
 * import behind a runtime `if` would still ship the code.
 *
 * It reads the `define` rather than `import.meta.env.VITE_DEV_LOGIN` directly
 * because the two are not equivalent: Vite substitutes both, but only the
 * `define` is used consistently across `auth/config.ts`, `App.tsx` and
 * `AuthProvider.tsx`, so there is one switch rather than four that can drift.
 */
const DEV_SWITCHER_ENABLED = __DEV_LOGIN__;
const DevUserSwitcher = DEV_SWITCHER_ENABLED
  ? lazy(() => import('@/components/DevUserSwitcher'))
  : null;

export const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/opportunities', label: 'Opportunities', icon: Briefcase },
  { to: '/accounts', label: 'Accounts', icon: Building2 },
  { to: '/meetings', label: 'Meetings', icon: CalendarDays },
  { to: '/actions', label: 'Actions', icon: CheckSquare },
  { to: '/log', label: 'Quick log', icon: Smartphone },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  // Directly above Admin. Admin is adminOnly, so for everyone else Strategy is
  // the last item. Every tier sees the link; the list behind it holds only the
  // accounts the server says the caller may read a strategy for.
  { to: '/strategy', label: 'Strategy', icon: Target },
  { to: '/admin', label: 'Admin', icon: Settings, adminOnly: true },
] as const;

export function AppShell({ children }: { children: ReactNode }): React.ReactElement {
  const { data: me } = useMe();
  const { signOut } = useAuth();

  // Hiding the Admin link is CONVENIENCE ONLY. §2 rule 1: authorization is
  // enforced server-side on every request; the frontend hides UI so people are
  // not offered things that will fail, not as a control.
  const nav = NAV.filter(
    (item) => !('adminOnly' in item && item.adminOnly) || me?.tier === 'Admin',
  );

  return (
    <div className="min-h-screen bg-background">
      <NewVersionBanner />

      <div className="flex min-h-screen">
        <nav aria-label="Main" className="w-56 shrink-0 border-r bg-card">
          <div className="px-4 py-5">
            <span className="text-sm font-semibold tracking-tight">Menlo Funnel</span>
          </div>
          <ul className="space-y-1 px-2">
            {nav.map(({ to, label, icon: Icon, ...rest }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={'end' in rest ? rest.end : false}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-accent font-medium text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center justify-end gap-4 border-b px-6">
            {/*
              The palette opens on Ctrl/Cmd+K from anywhere; this is the
              discoverable half of the same thing. Pasting an opp number out of
              a digest email jumps straight to that deal.
            */}
            <button
              type="button"
              onClick={() =>
                document.dispatchEvent(
                  new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }),
                )
              }
              className="mr-auto flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
            >
              <Search className="h-3.5 w-3.5" aria-hidden="true" />
              Search
              <kbd className="rounded border px-1 text-[10px]">Ctrl K</kbd>
            </button>

            <ThemeToggle />

            {DevUserSwitcher !== null && (
              <Suspense fallback={null}>
                <DevUserSwitcher />
              </Suspense>
            )}

            {me && (
              <div className="text-right text-xs leading-tight">
                <div className="font-medium">{me.user.display_name}</div>
                <div className="text-muted-foreground">
                  {me.tier}
                  {me.roles.length > 0 ? ` · ${me.roles.join(', ')}` : ''}
                </div>
              </div>
            )}

            <Button size="sm" variant="ghost" onClick={signOut}>
              Sign out
            </Button>
          </header>

          <main className="min-w-0 flex-1 p-6">{children}</main>
          <CommandPalette />
        </div>
      </div>
    </div>
  );
}
